import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authApi } from '../lib/api'
import type { AdminUser, AuthResponse } from '../types'

/** What the password step returned: signed in, or an authenticator step is needed first. */
export type LoginStep =
  | { kind: 'done' }
  | { kind: 'totp'; challengeToken: string }
  | { kind: 'totp-setup'; challengeToken: string }

interface AuthContextValue {
  currentAdmin: AdminUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<LoginStep>
  completeLogin: (auth: AuthResponse) => void
  logout: () => void
}

export const AuthContext = createContext<AuthContextValue>({
  currentAdmin: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ kind: 'done' }),
  completeLogin: () => {},
  logout: () => {},
})

export const useAuth = () => useContext(AuthContext)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentAdmin, setCurrentAdmin] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('queza_admin_token')
    const storedUser = localStorage.getItem('queza_admin_user')
    if (token && storedUser) {
      try {
        setCurrentAdmin(JSON.parse(storedUser))
      } catch {
        localStorage.removeItem('queza_admin_user')
      }
    }
    // Verify token with server
    if (token) {
      authApi
        .me()
        .then((res) => {
          if (res.data.success && res.data.data) {
            setCurrentAdmin(res.data.data)
            localStorage.setItem('queza_admin_user', JSON.stringify(res.data.data))
          } else {
            localStorage.removeItem('queza_admin_token')
            localStorage.removeItem('queza_admin_user')
            setCurrentAdmin(null)
          }
        })
        .catch(() => {
          // token is invalid or server unreachable; keep stored user for dev
        })
        .finally(() => setIsLoading(false))
    } else {
      setIsLoading(false)
    }
  }, [])

  // Permissions can change while someone is signed in (a role is edited) —
  // re-read them when the tab regains focus and every 2 minutes.
  useEffect(() => {
    const refresh = () => {
      if (!localStorage.getItem('queza_admin_token')) return
      authApi
        .me()
        .then((res) => {
          if (res.data.success && res.data.data) {
            setCurrentAdmin(res.data.data)
            localStorage.setItem('queza_admin_user', JSON.stringify(res.data.data))
          }
        })
        .catch(() => {})
    }
    const onFocus = () => document.visibilityState === 'visible' && refresh()
    document.addEventListener('visibilitychange', onFocus)
    const timer = window.setInterval(refresh, 120_000)
    return () => {
      document.removeEventListener('visibilitychange', onFocus)
      window.clearInterval(timer)
    }
  }, [])

  const completeLogin = useCallback((auth: AuthResponse) => {
    localStorage.setItem('queza_admin_token', auth.token)
    localStorage.setItem('queza_admin_user', JSON.stringify(auth.admin))
    setCurrentAdmin(auth.admin)
  }, [])

  const login = useCallback(
    async (email: string, password: string): Promise<LoginStep> => {
      const res = await authApi.login(email, password)
      const data = res.data.data
      if (!res.data.success || !data) throw new Error(res.data.error || 'Login failed')
      if ('twoFactorRequired' in data) return { kind: 'totp', challengeToken: data.challengeToken }
      if ('twoFactorSetupRequired' in data) return { kind: 'totp-setup', challengeToken: data.challengeToken }
      completeLogin(data)
      return { kind: 'done' }
    },
    [completeLogin]
  )

  const logout = useCallback(() => {
    authApi.logout().catch(() => {})
    localStorage.removeItem('queza_admin_token')
    localStorage.removeItem('queza_admin_user')
    setCurrentAdmin(null)
    window.location.href = '/login'
  }, [])

  const value: AuthContextValue = {
    currentAdmin,
    isAuthenticated: !!currentAdmin,
    isLoading,
    login,
    completeLogin,
    logout,
  }

  return React.createElement(AuthContext.Provider, { value }, children)
}
